import { useEffect } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import {
  ANNOTATION_PENDING_LIMIT_COUNT_FF,
  REALLOCATION_ORDERED_MOVABLE_COUNT_FF,
  isActiveRow,
  rowHasError,
} from './reallocation.utils';
import type { ReassignmentRowState } from './types';
import { useGetMovableEncounterCountQuery } from '../../api/adminProjectsApiSlice';
import type { AnnotatorBatchBreakdown, ReallocationBatchKey } from '../../types/response';

interface UseReassignmentRowProps {
  projectId: string;
  batch: ReallocationBatchKey;
  row: ReassignmentRowState;
  rows: ReassignmentRowState[];
  perAnnotator: AnnotatorBatchBreakdown[];
  onChange: (patch: Partial<ReassignmentRowState>) => void;
}

export const useReassignmentRow = ({
  projectId,
  batch,
  row,
  rows,
  perAnnotator,
  onChange,
}: UseReassignmentRowProps) => {
  // From-annotators already paired with the current to in other rows.
  const usedFromIds = new Set(
    row.toAnnotatorId
      ? rows
          .filter(
            (r) => r.id !== row.id && r.toAnnotatorId === row.toAnnotatorId && r.fromAnnotatorId
          )
          .map((r) => r.fromAnnotatorId)
      : []
  );

  // Valid "to" targets for a given from-annotator: any non-AI annotator other
  // than the from itself that this from isn't already paired with in another
  // row. (Unsaved annotators can receive moves, so they remain eligible.)
  const toOptionsFor = (fromId: string) => {
    const usedTo = new Set(
      rows
        .filter((r) => r.id !== row.id && r.fromAnnotatorId === fromId && r.toAnnotatorId)
        .map((r) => r.toAnnotatorId)
    );
    return perAnnotator.filter(
      (a) => !a.isAiAgent && a.annotatorId !== fromId && !usedTo.has(a.annotatorId)
    );
  };

  // Unsaved (newly added) annotators can receive moves but cannot give them.
  // A saved annotator is also hidden once it has been paired with every possible
  // target (exhausted source) — but the row's current selection is always kept
  // so the Select can still render it.
  const fromOptions = perAnnotator.filter(
    (a) =>
      !a.isAiAgent &&
      !a.isUnsaved &&
      a.annotatorId !== row.toAnnotatorId &&
      !usedFromIds.has(a.annotatorId) &&
      (a.annotatorId === row.fromAnnotatorId || toOptionsFor(a.annotatorId).length > 0)
  );
  const toOptions = toOptionsFor(row.fromAnnotatorId);

  const toIsUnsaved = Boolean(
    perAnnotator.find((a) => a.annotatorId === row.toAnnotatorId)?.isUnsaved
  );
  const bothSelected = Boolean(row.fromAnnotatorId && row.toAnnotatorId);
  const fromPending =
    perAnnotator.find((a) => a.annotatorId === row.fromAnnotatorId)?.pendingCount ?? 0;

  const orderedFixEnabled = Boolean(useFeatureFlagSplit(REALLOCATION_ORDERED_MOVABLE_COUNT_FF));
  const pendingLimitFixEnabled = Boolean(useFeatureFlagSplit(ANNOTATION_PENDING_LIMIT_COUNT_FF));
  // With the fix (ON): only rows ABOVE this one (added earlier) constrain its
  // cap — never rows below it. This makes causality one-directional: finishing
  // a later row can show a smaller cap for itself (correctly reflecting what
  // earlier rows already claimed), but can never retroactively invalidate an
  // earlier row that was already valid.
  // Legacy (OFF, default): ALL other rows are treated as siblings regardless
  // of order — completing either one of two rows sharing a from-annotator can
  // flip the other to an error state, forcing the user to re-edit rows they'd
  // already finished. This is the original pre-fix behavior.
  const priorRows = rows.slice(
    0,
    rows.findIndex((r) => r.id === row.id)
  );
  const legacySiblingRows = rows.filter((r) => r.id !== row.id);
  const contextRows = orderedFixEnabled ? priorRows : legacySiblingRows;

  // Sibling rows sent to the backend as context so it can return a
  // context-aware movable cap that accounts for:
  //   - sibling outflows from the same from-annotator (case 5)
  //   - sibling inflows into the from-annotator (case 6)
  // Changing a sibling row changes the cache key → automatic re-fetch.
  const siblingContext = contextRows.filter(isActiveRow).map((r) => ({
    fromAnnotatorId: r.fromAnnotatorId,
    toAnnotatorId: r.toAnnotatorId,
    count: r.count as number,
  }));

  // For an unsaved target there is no double-assignment overlap, so all of the
  // from-annotator's effective pending can move — skip the backend call.
  // `currentData` (not `data`) so the value is scoped to the active (from → to)
  // pair: it drops to undefined while a new pair is in flight rather than
  // retaining the previous pair's count. That guarantees the sync effect below
  // re-fires even when two pairs happen to return the same movable count.
  const { currentData: movableData, isFetching: isFetchingMovable } =
    useGetMovableEncounterCountQuery(
      {
        projectId,
        batch,
        fromAnnotatorId: row.fromAnnotatorId,
        toAnnotatorId: row.toAnnotatorId,
        context: siblingContext,
      },
      { skip: !bothSelected || toIsUnsaved }
    );

  // Sibling rows moving encounters into the from-annotator expand its effective
  // pool (case 6). Gated by `cp_annotation_pending_limit_count_fix`: when ON,
  // draft/unsubmitted sibling rows never grow the from-annotator's pool — the
  // cap stays bounded by its currently allocated (DB-committed) pending count.
  const incomingToFrom = pendingLimitFixEnabled
    ? 0
    : contextRows
        .filter((r) => isActiveRow(r) && r.toAnnotatorId === row.fromAnnotatorId)
        .reduce((sum, r) => sum + (r.count ?? 0), 0);
  // Sibling rows moving encounters out of the same from-annotator shrink its
  // effective pool. Unlike the backend's context-aware movable cap (which must
  // account for each target's own double-assignment exclusions), this is exact
  // here: an unsaved To annotator has no prior assignments at all, so its
  // eligible pool is the from-annotator's entire pending set — any sibling
  // outflow necessarily overlaps it 1:1. Only applied for the fix (ON) — the
  // legacy path never subtracted sibling outflow here (matching its original,
  // pre-fix behavior).
  const usedSameFrom = orderedFixEnabled
    ? contextRows
        .filter((r) => r.fromAnnotatorId === row.fromAnnotatorId)
        .reduce((sum, r) => sum + (r.count ?? 0), 0)
    : 0;
  const effectivePending = Math.max(0, fromPending - usedSameFrom + incomingToFrom);

  const fetchedMovable = !bothSelected
    ? null
    : toIsUnsaved
    ? effectivePending
    : movableData?.data?.movableCount ?? null;

  // Push the fetched movable cap up into wizard state (drives preview + validation).
  useEffect(() => {
    if (fetchedMovable !== row.movable) {
      onChange({ movable: fetchedMovable });
    }
    // row.movable is included so an externally-reset cap (e.g. a From/To change
    // clears it to null) is reconciled even when fetchedMovable is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedMovable, row.movable]);

  const annotatorName = (id: string) =>
    perAnnotator.find((a) => a.annotatorId === id)?.annotatorName ?? '';

  const hasError = rowHasError(rows, row, perAnnotator, orderedFixEnabled, pendingLimitFixEnabled);

  return {
    usedFromIds,
    toOptionsFor,
    fromOptions,
    toOptions,
    toIsUnsaved,
    bothSelected,
    fromPending,
    orderedFixEnabled,
    pendingLimitFixEnabled,
    priorRows,
    legacySiblingRows,
    contextRows,
    siblingContext,
    movableData,
    isFetchingMovable,
    incomingToFrom,
    usedSameFrom,
    effectivePending,
    fetchedMovable,
    annotatorName,
    hasError,
  };
};
